import { useCallback, useEffect, useRef, useState } from 'react';
import { getHexTileMapKey, type HexTileCoordinates, type HexTileMapKey } from '@/game/hexmap';
import { isCity } from '@/game/sim';
import type { Frame, FrameStore } from '@/game/view/frame';
import {
  type GroupTap,
  groupFocus,
  isDoubleTap,
  isExactSelection,
  ownGroups,
} from '@/game/view/groups';
import {
  isSlotSelection,
  resolveDigit,
  slotFocus,
  assignSlot as withSlot,
} from '@/game/view/slots';
import { tickRoster } from '@/game/view/tickCache';
import { nextIdle } from '@/game/view/workerRoster';
import { type UiStore, useUi } from '../ui/store';

function toggleId(ids: readonly string[], id: string): readonly string[] {
  return ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id];
}

/** `ids` added to the selection, or taken out when every one is already in it. */
function toggleAll(prev: readonly string[], ids: readonly string[]): readonly string[] {
  return ids.every((id) => prev.includes(id))
    ? prev.filter((id) => !ids.includes(id))
    : [...prev, ...ids.filter((id) => !prev.includes(id))];
}

/**
 * Selecting own units and buildings (map, rosters, group keys) and keeping
 * the selection valid: only own units can be selected (a rival's would show
 * its destination, even into fog, and actions that do nothing; a click on
 * one pins its tile in the tile info panel instead), the unit and building
 * selections clear each other, and units or buildings that are gone or no
 * longer ours drop out.
 */
export function useSelection(
  ui: UiStore,
  frames: FrameStore,
  frame: Frame,
  panTo: (pos: HexTileCoordinates) => void
) {
  const selectedUnitIds = useUi(ui, (s) => s.selectedUnitIds);
  const selectedBuildingId = useUi(ui, (s) => s.selectedBuildingId);
  const setUnits = useCallback(
    (next: (prev: readonly string[]) => readonly string[]) =>
      ui.set((s) => ({ selectedUnitIds: next(s.selectedUnitIds) })),
    [ui]
  );
  // A tile the player clicked to inspect a rival there (shown in the tile
  // info panel while the cursor is off the map).
  const [inspectKey, setInspectKey] = useState<HexTileMapKey | null>(null);

  // A selected own building (CityPanel, or BuildingPanel for farms etc.) and
  // the unit selection (any mix of own units) clear each other.
  useEffect(() => {
    if (selectedUnitIds.length > 0) ui.set({ selectedBuildingId: null });
  }, [selectedUnitIds, ui]);
  // Gone or no longer ours (an exposed spy we had selected as one of our
  // workers). Judged on the view, so a still-disguised spy stays selectable
  // for Expose spy.
  useEffect(() => {
    const units = frame.view.units;
    const mine = (id: string) => units[id]?.owner === frame.playerId;
    if (!selectedUnitIds.every(mine)) ui.set({ selectedUnitIds: selectedUnitIds.filter(mine) });
  }, [frame, selectedUnitIds, ui]);
  // The selected building once it's gone (destroyed).
  useEffect(() => {
    if (selectedBuildingId && !frame.view.buildings[selectedBuildingId]) {
      ui.set({ selectedBuildingId: null });
    }
  }, [frame, selectedBuildingId, ui]);

  // A click on a unit's model: selects just it (a second click keeps it
  // selected, so e.g. a settler's Found City bar stays up), shift adds or
  // removes it.
  const selectUnit = useCallback(
    (id: string, additive: boolean) => setUnits((prev) => (additive ? toggleId(prev, id) : [id])),
    [setUnits]
  );
  const selectOnMap = useCallback(
    (id: string, additive: boolean) => {
      const { view, playerId } = frames.get();
      const unit = view.units[id];
      if (unit && unit.owner !== playerId) setInspectKey(getHexTileMapKey(unit.pos));
      if (unit?.owner !== playerId) return;
      selectUnit(id, additive);
    },
    [frames, selectUnit]
  );
  // Shift-click on an automated group: add it, or remove it when all selected.
  const toggleUnits = useCallback(
    (ids: string[]) => setUnits((prev) => toggleAll(prev, ids)),
    [setUnits]
  );
  // A type header / Select army: select them all, or with shift add (or remove) them.
  const selectMany = useCallback(
    (ids: string[], additive: boolean) =>
      setUnits((prev) => (additive ? toggleAll(prev, ids) : ids)),
    [setUnits]
  );

  // Group N picked by its key or its Units-panel row: selects it (shift adds
  // or removes); the same group again within GROUP_DOUBLE_TAP_MS, or a plain
  // press while that group is exactly the selection (however long since), goes there.
  const groupTapRef = useRef<GroupTap | null>(null);
  const tapGroup = useCallback(
    (number: number, additive: boolean) => {
      const { view, playerId } = frames.get();
      const group = ownGroups(view, playerId).find((g) => g.number === number);
      if (!group) return;
      const now = performance.now();
      if (
        isDoubleTap(groupTapRef.current, number, now) ||
        (!additive && isExactSelection(group, ui.get().selectedUnitIds))
      ) {
        groupTapRef.current = null;
        const pos = groupFocus(group);
        if (pos) panTo(pos);
        return;
      }
      groupTapRef.current = { number, time: now };
      selectMany(
        group.units.map((u) => u.id),
        additive
      );
    },
    [frames, selectMany, panTo, ui]
  );

  // A digit key or groups bar tile: control slot N if it has units, else
  // formation Group N. Same select / shift-add / double-tap-or-again pan as tapGroup.
  const tapDigit = useCallback(
    (number: number, additive: boolean) => {
      const { view, playerId } = frames.get();
      const target = resolveDigit(
        view,
        playerId,
        ui.get().slots,
        ownGroups(view, playerId),
        number
      );
      if (!target) return;
      if (target.kind === 'group') {
        tapGroup(number, additive);
        return;
      }
      const now = performance.now();
      if (
        isDoubleTap(groupTapRef.current, number, now) ||
        (!additive && isSlotSelection(target.units, ui.get().selectedUnitIds))
      ) {
        groupTapRef.current = null;
        const pos = slotFocus(target.units);
        if (pos) panTo(pos);
        return;
      }
      groupTapRef.current = { number, time: now };
      selectMany(
        target.units.map((u) => u.id),
        additive
      );
    },
    [frames, tapGroup, selectMany, panTo, ui]
  );
  // Ctrl+N: the own units selected become slot N.
  const assignSlot = useCallback(
    (number: number) => {
      const { view, playerId } = frames.get();
      ui.set((s) => ({ slots: withSlot(s.slots, number, view, playerId, s.selectedUnitIds) }));
    },
    [frames, ui]
  );

  const selectBuilding = useCallback(
    (id: string) => ui.set({ selectedBuildingId: id, selectedUnitIds: [] }),
    [ui]
  );
  // Buildings list: select, and on a plain click go there too. A city site
  // has no panel (as on the map): it only goes there.
  const selectListedBuilding = useCallback(
    (id: string, pan: boolean) => {
      const { view, rules } = frames.get();
      const b = view.buildings[id];
      if (!b) return;
      if (isCity(b, rules) && b.buildProgress < 1) {
        panTo(b.pos);
        return;
      }
      ui.set({ placementMode: null });
      selectBuilding(id);
      if (pan) panTo(b.pos);
    },
    [frames, selectBuilding, panTo, ui]
  );
  const focusUnit = useCallback(
    (id: string) => {
      const unit = frames.get().view.units[id];
      if (unit) panTo(unit.pos);
    },
    [frames, panTo]
  );
  // The `.` key / Idle button: the next idle worker after the selected one.
  const cycleIdle = useCallback(() => {
    const { view, playerId, rules, terrain } = frames.get();
    const id = nextIdle(
      tickRoster(view, { terrain, rules, playerId }).idle,
      ui.get().selectedUnitIds
    );
    const unit = id ? view.units[id] : undefined;
    if (!unit) return;
    selectMany([unit.id], false);
    panTo(unit.pos);
  }, [frames, ui, selectMany, panTo]);
  // A Cities chip's ⌖, the city panel's ⌖ and idle-worker notifications go there.
  const panToCity = useCallback(
    (id: string) => {
      const city = frames.get().view.buildings[id];
      if (city) panTo(city.pos);
    },
    [frames, panTo]
  );

  return {
    selectedUnitIds,
    selectedBuildingId,
    inspectKey,
    setInspectKey,
    selectUnit,
    selectOnMap,
    toggleUnits,
    selectMany,
    tapGroup,
    tapDigit,
    assignSlot,
    selectBuilding,
    selectListedBuilding,
    focusUnit,
    cycleIdle,
    panToCity,
  };
}

export type Selection = ReturnType<typeof useSelection>;
